import dynamic from 'next/dynamic';
import type { IChartingLibraryWidget } from 'public/static/charting_library/charting_library';
import { useState } from 'react';

import { ChartLoadingSkeleton } from '@/components/Charts/ChartLoadingSkeleton';
import { useHorizontalLine } from '@/components/Charts/hooks/useHorizontalLine';
import type { LimitPriceTokenSide } from '@/components/TransactionModule/LimitMode/LimitPriceShortcuts';

import { TokenResult } from '@/utils/0x/token-registry.types';
import { primitiveColors } from '@/utils/generated/primitiveColors';

import type { LimitChartLine } from './TradeLimitChart';

const TradingViewChart = dynamic(
  () => import('@/components/Charts/index').then(({ TradingViewChart }) => TradingViewChart),
  {
    ssr: false,
    loading: () => <ChartLoadingSkeleton />,
  },
);

type TradeLimitTradingViewChartProps = {
  buyToken: TokenResult | undefined;
  sellToken: TokenResult | undefined;
  side: LimitPriceTokenSide;
  line: LimitChartLine;
  isExpanded: boolean;
  setIsExpanded: (isExpanded: boolean) => void;
};

/** TradingView widget with the order-price line drawn as a horizontal shape. */
export const TradeLimitTradingViewChart = ({
  buyToken,
  sellToken,
  side,
  line,
  isExpanded,
  setIsExpanded,
}: TradeLimitTradingViewChartProps) => {
  const [widget, setWidget] = useState<IChartingLibraryWidget | null>(null);
  useHorizontalLine(widget, {
    enabled: line.enabled,
    draggable: false,
    text: line.label,
    price: line.price,
    lineColor: primitiveColors[line.color],
    lineStyle: 1,
    textColor: primitiveColors[line.color],
    horzLabelsAlign: 'right',
    onPriceChange: line.onPriceChange,
  });

  return (
    <TradingViewChart
      onWidgetReady={setWidget}
      activeToken={buyToken}
      inactiveToken={sellToken}
      expanded={isExpanded}
      setIsExpanded={setIsExpanded}
      isTokenPageLoading={false}
      priceView={side === 'sell' ? 'pair-inverse' : 'pair'}
      disableMarketView={true}
    />
  );
};
